import { html, css, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { PageElement } from "../lib/page-element.js";
import { shared } from "../styles.js";
import { search, type SearchResponse, type SortKey, type FacetCount } from "../api/registry.js";
import { humanError } from "../api/errors.js";
import { isShareableKind } from "../lib/kinds.js";
import { previewType } from "../lib/preview.js";
import { formatCount, relativeTime } from "../lib/format.js";
import { chevronDown } from "../lib/icons.js";
import { navigate } from "../nav.js";
import "../components/ibcs-preview.js";

const SORTS: { key: SortKey; label: string }[] = [
  { key: "relevance", label: "Relevance" },
  { key: "pulls", label: "Installs" },
  { key: "updated", label: "Newest" },
  { key: "name", label: "Name" },
];

/**
 * page-search is the browse + search page (R8/S1) — the design's "Explore" screen.
 * It reads the query from the URL (passed in as `search` so it re-fetches on every
 * navigation), calls GET /api/registry/search, and renders the result set as a
 * framed two-pane layout: a left filter sidebar (categories + kind/scope/tag facets)
 * and a dense data table with IBCS preview thumbnails. Anonymous — no auth required.
 */
@customElement("page-search")
export class PageSearch extends PageElement {
  static override styles = [
    shared,
    css`
      .frame {
        border: 1px solid var(--border);
        border-radius: 16px;
        overflow: hidden;
        background: var(--surface);
        display: grid;
        grid-template-columns: 236px 1fr;
      }
      @media (max-width: 820px) {
        .frame {
          grid-template-columns: 1fr;
        }
        aside {
          border-right: 0;
          border-bottom: 1px solid var(--border);
        }
      }

      aside {
        border-right: 1px solid var(--border);
        padding: 18px 16px;
        display: flex;
        flex-direction: column;
        gap: 22px;
      }
      .facet-label {
        font: 600 10.5px/1 var(--mono);
        letter-spacing: 0.1em;
        color: var(--text-faint);
        margin-bottom: 10px;
        padding: 0 4px;
      }
      .cat-list {
        display: flex;
        flex-direction: column;
        gap: 1px;
      }
      .cat {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 7px 9px;
        border-radius: 7px;
        cursor: pointer;
        border: 0;
        background: none;
        width: 100%;
        text-align: left;
        font: 500 12.5px/1 var(--sans);
        color: var(--text-dim);
      }
      .cat:hover {
        background: var(--surface-2);
      }
      .cat.active {
        background: var(--surface-3);
        color: var(--text);
        font-weight: 600;
      }
      .cat .count {
        font: 500 11px/1 var(--mono);
        color: var(--text-faint);
      }

      .checks {
        display: flex;
        flex-direction: column;
        gap: 9px;
        padding: 0 4px;
      }
      .check {
        display: flex;
        align-items: center;
        gap: 9px;
        cursor: pointer;
        border: 0;
        background: none;
        width: 100%;
        text-align: left;
        padding: 0;
      }
      .box {
        width: 15px;
        height: 15px;
        border-radius: 4px;
        border: 1.5px solid var(--border-strong);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .check.on .box {
        background: var(--brand);
        border-color: var(--brand);
      }
      .box svg {
        display: none;
      }
      .check.on .box svg {
        display: block;
      }
      .check-label {
        font: 500 12.5px/1 var(--sans);
        color: var(--text-dim);
      }
      .check.on .check-label {
        color: var(--text);
      }
      .check .count {
        margin-left: auto;
        font: 500 11px/1 var(--mono);
        color: var(--text-faint);
      }

      .promo {
        margin-top: auto;
        border: 1px solid var(--border);
        border-radius: 9px;
        padding: 11px 12px;
        background: var(--surface-2);
        display: block;
      }
      .promo .t {
        font: 600 12px/1 var(--sans);
        color: var(--text);
        margin-bottom: 5px;
      }
      .promo .d {
        font: 400 11.5px/1.5 var(--sans);
        color: var(--text-dim);
      }
      .promo code {
        font: 500 11px/1 var(--mono);
        color: var(--text);
      }

      .main {
        min-width: 0;
        background: var(--surface);
      }
      .result-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 14px 20px;
        border-bottom: 1px solid var(--border);
      }
      .result-title {
        display: flex;
        align-items: baseline;
        gap: 9px;
        min-width: 0;
      }
      .result-title .h {
        font: 600 15px/1.2 var(--sans);
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .result-title .n {
        font: 500 12.5px/1 var(--mono);
        color: var(--text-faint);
        white-space: nowrap;
      }
      .sort {
        display: flex;
        align-items: center;
        gap: 6px;
        height: 30px;
        padding: 0 6px 0 11px;
        border: 1px solid var(--border);
        border-radius: 8px;
        color: var(--text-faint);
        flex-shrink: 0;
      }
      .sort label {
        margin: 0;
        font: 400 12.5px/1 var(--sans);
        color: var(--text-faint);
      }
      .sort select {
        width: auto;
        border: 0;
        background: transparent;
        appearance: none;
        -webkit-appearance: none;
        padding: 0 2px;
        font: 500 12.5px/1 var(--sans);
        color: var(--text);
      }
      .sort select:focus {
        outline: none;
      }

      .col-head {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 9px 20px;
        border-bottom: 1px solid var(--border);
        background: var(--surface-2);
      }
      .col-head span {
        font: 600 10.5px/1 var(--mono);
        letter-spacing: 0.08em;
        color: var(--text-dim);
        flex-shrink: 0;
      }

      .row {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px 20px;
        border-bottom: 1px solid var(--border);
      }
      .row:hover {
        background: var(--surface-2);
        text-decoration: none;
      }
      .thumb {
        width: 88px;
        height: 46px;
        border: 1px solid var(--border);
        border-radius: 7px;
        background: var(--surface-2);
        padding: 5px 7px;
        flex-shrink: 0;
      }
      .c-component {
        flex: 1;
        min-width: 0;
      }
      .c-name {
        font: 600 13.5px/1.2 var(--mono);
        color: var(--text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .c-desc {
        font: 400 12px/1.4 var(--sans);
        color: var(--text-dim);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-top: 3px;
      }
      .c-tags {
        width: 150px;
        flex-shrink: 0;
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
      }
      .c-tags .chip {
        margin: 0;
      }
      .c-installs {
        width: 70px;
        flex-shrink: 0;
        text-align: right;
        font: 500 12.5px/1 var(--mono);
        color: var(--text);
      }
      .c-ver {
        width: 60px;
        flex-shrink: 0;
        font: 500 12px/1 var(--mono);
        color: var(--text-dim);
      }
      .c-kind {
        width: 92px;
        flex-shrink: 0;
        font: 500 11.5px/1 var(--mono);
        color: var(--text-faint);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .c-updated {
        width: 66px;
        flex-shrink: 0;
        text-align: right;
        font: 400 11.5px/1 var(--sans);
        color: var(--text-faint);
      }
      @media (max-width: 820px) {
        .c-tags,
        .c-ver,
        .c-kind {
          display: none;
        }
      }
      @media (max-width: 600px) {
        .col-head,
        .c-updated {
          display: none;
        }
        .row {
          padding: 12px 16px;
          gap: 12px;
        }
        .thumb {
          width: 64px;
          height: 34px;
          padding: 3px 5px;
        }
        .c-installs {
          width: auto;
        }
        .result-head {
          flex-wrap: wrap;
          padding: 12px 16px;
        }
        .pager {
          padding: 12px 16px;
        }
      }

      .pager {
        display: flex;
        gap: var(--sp-2);
        align-items: center;
        padding: 16px 20px;
      }
      .empty {
        padding: 48px;
        text-align: center;
        color: var(--text-dim);
      }
      .loading {
        padding: 14px 20px;
        color: var(--text-dim);
        font-size: var(--fs-2);
      }
    `,
  ];

  @property() search = "";
  @state() private result: SearchResponse | null = null;
  @state() private loading = false;
  @state() private errorMsg = "";

  private get params(): URLSearchParams {
    return new URLSearchParams(this.search.startsWith("?") ? this.search.slice(1) : this.search);
  }

  override willUpdate(changed: PropertyValues) {
    if (changed.has("search")) {
      void this.fetch();
    }
  }

  private async fetch() {
    const p = this.params;
    const q = p.get("q") ?? "";
    this.setTitle(q ? `Search: ${q}` : "Explore");
    this.loading = true;
    this.errorMsg = "";
    try {
      this.result = await search({
        q,
        kind: p.getAll("kind"),
        tag: p.getAll("tag"),
        scope: p.getAll("scope"),
        category: p.getAll("category"),
        stable: p.get("stable") === "1",
        sort: (p.get("sort") as SortKey) || "relevance",
        page: Number(p.get("page") || "1"),
        perPage: 20,
        facets: true,
      });
    } catch (e) {
      this.errorMsg = humanError(e);
      this.result = null;
    } finally {
      this.loading = false;
    }
  }

  /** toggleMulti adds/removes a value for a repeatable facet param and navigates. */
  private toggleMulti(key: string, value: string) {
    const p = this.params;
    const existing = p.getAll(key);
    p.delete(key);
    if (existing.includes(value)) {
      for (const v of existing) if (v !== value) p.append(key, v);
    } else {
      for (const v of existing) p.append(key, v);
      p.append(key, value);
    }
    p.delete("page");
    navigate("/?" + p.toString());
  }

  private clearKey(key: string) {
    const p = this.params;
    p.delete(key);
    p.delete("page");
    navigate("/?" + p.toString());
  }

  private setParam(key: string, value: string) {
    const p = this.params;
    if (value) p.set(key, value);
    else p.delete(key);
    p.delete("page");
    navigate("/?" + p.toString());
  }

  private goPage(page: number) {
    const p = this.params;
    p.set("page", String(page));
    navigate("/?" + p.toString());
  }

  private renderCategories(values: FacetCount[]) {
    const active = this.params.getAll("category");
    return html`
      <div>
        <div class="facet-label">CATEGORIES</div>
        <div class="cat-list">
          <button
            class="cat ${active.length === 0 ? "active" : ""}"
            aria-label="All categories"
            aria-pressed=${active.length === 0 ? "true" : "false"}
            @click=${() => this.clearKey("category")}
          >
            <span>All</span>
            <span class="count">${formatCount(this.result?.totalItems)}</span>
          </button>
          ${values.map(
            (f) => html`<button
              class="cat ${active.includes(f.value) ? "active" : ""}"
              aria-label=${`Category: ${f.value}`}
              aria-pressed=${active.includes(f.value) ? "true" : "false"}
              @click=${() => this.toggleMulti("category", f.value)}
            >
              <span>${f.value}</span>
              <span class="count">${f.count}</span>
            </button>`,
          )}
        </div>
      </div>
    `;
  }

  private renderChecks(title: string, key: string, values: FacetCount[]) {
    if (!values.length) return "";
    const active = this.params.getAll(key);
    return html`
      <div>
        <div class="facet-label">${title}</div>
        <div class="checks">
          ${values.map((f) => {
            const on = active.includes(f.value);
            return html`<button
              class="check ${on ? "on" : ""}"
              role="checkbox"
              aria-checked=${on ? "true" : "false"}
              aria-label=${`${title} filter: ${f.value}`}
              @click=${() => this.toggleMulti(key, f.value)}
            >
              <span class="box"
                ><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="var(--brand-contrast)" stroke-width="3.5" aria-hidden="true">
                  <path d="M5 12l5 5L20 6"></path></svg
              ></span>
              <span class="check-label">${f.value}</span>
              <span class="count">${f.count}</span>
            </button>`;
          })}
        </div>
      </div>
    `;
  }

  private renderSort() {
    const sort = this.params.get("sort") || "relevance";
    return html`<div class="sort">
      <label for="sortsel">Sort:</label>
      <select
        id="sortsel"
        aria-label="Sort"
        @change=${(e: Event) => this.setParam("sort", (e.target as HTMLSelectElement).value)}
      >
        ${SORTS.map((s) => html`<option value=${s.key} ?selected=${sort === s.key}>${s.label}</option>`)}
      </select>
      ${chevronDown(12)}
    </div>`;
  }

  override render() {
    const p = this.params;
    const q = p.get("q") ?? "";
    const r = this.result;
    const facets = r?.facets;
    const kindFacets = (facets?.kind ?? []).filter((f) => isShareableKind(f.value));
    const headTitle = q ? `Results for “${q}”` : "All components";

    return html`
      <div class="wrap">
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        <div class="frame">
          <aside aria-label="Filters">
            ${this.renderCategories(facets?.category ?? [])}
            ${this.renderChecks("KIND", "kind", kindFacets)}
            ${this.renderChecks("SCOPE", "scope", facets?.scope ?? [])}
            ${this.renderChecks("TAG", "tag", facets?.tag ?? [])}
            <div class="promo">
              <div class="t">Publish your own</div>
              <div class="d">Push a YAML report element to the registry with <code>bino publish</code>.</div>
            </div>
          </aside>

          <div class="main">
            <div class="result-head">
              <div class="result-title">
                <h1 class="h">${headTitle}</h1>
                <span class="n">${formatCount(r?.totalItems)}</span>
              </div>
              ${this.renderSort()}
            </div>
            <div class="col-head" aria-hidden="true">
              <span style="width:88px"></span>
              <span style="flex:1;min-width:0">COMPONENT</span>
              <span style="width:150px">TAGS</span>
              <span style="width:70px;text-align:right">INSTALLS</span>
              <span style="width:60px">VER</span>
              <span style="width:92px">KIND</span>
              <span style="width:66px;text-align:right">UPDATED</span>
            </div>

            ${this.loading && !r ? html`<div class="loading">Searching…</div>` : ""}
            ${(r?.items ?? []).map((it) => {
              const coord = it.scope.replace(/^@/, "");
              const desc = it.title || it.description || "";
              return html`<a
                class="row"
                href=${`/p/${coord}/${it.name}`}
                aria-label=${desc ? `${it.package} — ${desc}` : it.package}
              >
                <div class="thumb">
                  <ibcs-preview type=${previewType(it.kind, it.category)}></ibcs-preview>
                </div>
                <div class="c-component">
                  <div class="c-name">${it.package}</div>
                  <div class="c-desc">${desc}</div>
                </div>
                <div class="c-tags">
                  ${(it.tags ?? []).slice(0, 2).map((t) => html`<span class="chip">${t}</span>`)}
                </div>
                <span class="c-installs">${formatCount(it.pulls30d)}</span>
                <span class="c-ver">${it.latestVersion || "–"}</span>
                <span class="c-kind">${it.kind}</span>
                <span class="c-updated">${relativeTime(it.updatedAt)}</span>
              </a>`;
            })}
            ${r && r.totalItems === 0 && !this.loading
              ? html`<div class="empty">No packages found.</div>`
              : ""}
            ${r && r.totalPages > 1
              ? html`<div class="pager">
                  <button class="btn" ?disabled=${r.page <= 1} @click=${() => this.goPage(r.page - 1)}>
                    ← Prev
                  </button>
                  <span class="muted">Page ${r.page} / ${r.totalPages}</span>
                  <button class="btn" ?disabled=${r.page >= r.totalPages} @click=${() => this.goPage(r.page + 1)}>
                    Next →
                  </button>
                </div>`
              : ""}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-search": PageSearch;
  }
}
